import { RemovalBehavior, type UpdateFrequency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, cn, Dialog, Label, Select, SelectItem } from '@utima/ui';
import { Input, type TypedFormState } from '@utima/ui-informed';
import type { FieldState, FormApi } from 'informed';
import { Info, RefreshCw, Trash2 } from 'lucide-react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { LockedFeatureIcon } from '@/components';
import { Alert } from '@/components/Alert';
import { BasicForm } from '@/components/form/BasicForm';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { useBoundStore } from '@/store/store';

import type { AddSourcePanelHandle } from './addSourcePanelHandle';
import { useDocumentMutation } from '../../hooks/useDocumentMutation';
import { formatAutoUpdateCosts } from '../../lib/autoUpdateCostCopy';
import { UPDATE_FREQUENCY_OPTIONS } from '../../lib/updateFrequencyOptions';

const INHERIT_UPDATE_FREQUENCY = '__inherit__' as const;

export type ExternalDocumentFormProps = {
  chatIds: string[];
  availableForAllAgents: boolean;
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  projectedLimitReached: boolean;
  onPendingCountChange: (count: number) => void;
  folderId?: string | null;
  /** Renders as a settings-only panel: no own URL input and no own
   * Add/submit button — the shared "Online sources" row owns the URL and
   * drives submission via the imperative handle (Phase R6). */
  embedded?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export const AddExternalDocument = memo(
  forwardRef<AddSourcePanelHandle, ExternalDocumentFormProps>(
    function ExternalDocumentForm(
      {
        chatIds,
        availableForAllAgents,
        setOpen,
        defaultUrl,
        limitReached,
        projectedLimitReached,
        onPendingCountChange,
        folderId,
        embedded = false,
        onSubmittingChange,
      },
      ref,
    ) {
      const { t } = useLingui();
      const enumTranslations = useEnumTranslations();
      const { organization } = useCurrentOrganization();
      const { check: checkCost, update: updateCost } = formatAutoUpdateCosts(
        organization?.currency,
      );
      const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();
      const { createExternalDocument } = useDocumentMutation();
      const formApi = useRef<FormApi>(null);
      const [updateFrequency, setUpdateFrequency] =
        useState<UpdateFrequency | null>(null);

      const { externalDocumentUri, setExternalDocumentUri } = useBoundStore(
        useShallow(state => ({
          externalDocumentUri: state.externalDocumentUri,
          setExternalDocumentUri: state.setExternalDocumentUri,
        })),
      );

      const handleAdd = useCallback(() => {
        formApi.current?.submitForm();
        setExternalDocumentUri(undefined);
      }, [setExternalDocumentUri]);

      useImperativeHandle(ref, () => ({ submit: handleAdd }), [handleAdd]);

      // Seed once from the "Online sources" classifier's prefill so the pending
      // count reflects the URL already visible in the (uncontrolled) input below.
      useEffect(() => {
        if (defaultUrl && !externalDocumentUri) {
          setExternalDocumentUri(defaultUrl);
          onPendingCountChange(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      useEffect(() => {
        onSubmittingChange?.(createExternalDocument.isPending);
      }, [createExternalDocument.isPending, onSubmittingChange]);

      const handleSubmit = async ({
        values,
      }: TypedFormState<{ uri: string }>) => {
        const uri = embedded
          ? (defaultUrl ?? externalDocumentUri ?? '')
          : values.uri;

        await createExternalDocument.mutateAsync({
          chats: chatIds,
          availableForAllAgents,
          name: uri,
          updateFrequency: isKbAutoUpdatesLocked ? null : updateFrequency,
          uri,
          folderId: folderId ?? null,
        });

        setOpen(false);
      };

      const handleInformedInputChange = useCallback(
        (fieldState: FieldState) => {
          const currentUri = fieldState.value as string;
          setExternalDocumentUri(currentUri);
          const isValid = z.string().url().safeParse(currentUri).success;
          onPendingCountChange(isValid ? 1 : 0);
        },
        [setExternalDocumentUri, onPendingCountChange],
      );

      return (
        <>
          {limitReached && (
            <Alert
              variant='warning'
              title={t`Document Limit Reached`}
              className='mb-4'
            >
              <Trans>
                You have reached the maximum document limit for your plan.
              </Trans>
            </Alert>
          )}
          <BasicForm
            formApiRef={formApi}
            onSubmit={handleSubmit}
            className='h-full max-h-[500px] min-h-0 grow space-y-4'
          >
            {!embedded && (
              <Input
                name='uri'
                label={t`URL`}
                zodItemSchema={z.string().url()}
                placeholder='https://example.com/document.pdf'
                required
                onChange={handleInformedInputChange}
                defaultValue={externalDocumentUri ?? defaultUrl}
              />
            )}

            <details className='rounded-lg bg-muted/30 px-3 py-2'>
              <summary className='cursor-pointer text-xs text-muted-fg'>
                <Trans>Advanced settings</Trans>
              </summary>
              <div
                className={cn(
                  'mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2',
                  isKbAutoUpdatesLocked && 'opacity-50',
                )}
              >
                <div className='min-w-0'>
                  <div className='mb-2 flex items-center gap-2'>
                    <RefreshCw className='size-3.5 shrink-0 text-muted-fg' />
                    <Label className='text-xs font-medium'>
                      <Trans>Auto Refresh</Trans>
                    </Label>
                    <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                    <SimpleTooltip
                      delayDuration={0}
                      title={t`How often the content is re-scraped to check for changes. About ${checkCost} per page check, up to ${updateCost} if the page actually changes.`}
                    >
                      <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <div
                    className={cn(
                      isKbAutoUpdatesLocked && 'pointer-events-none',
                    )}
                  >
                    <Select
                      value={updateFrequency ?? INHERIT_UPDATE_FREQUENCY}
                      disabled={isKbAutoUpdatesLocked}
                      onValueChange={v =>
                        setUpdateFrequency(
                          v === INHERIT_UPDATE_FREQUENCY
                            ? null
                            : (v as UpdateFrequency),
                        )
                      }
                    >
                      <SelectItem value={INHERIT_UPDATE_FREQUENCY}>
                        <Trans>Inherit</Trans>
                      </SelectItem>
                      {UPDATE_FREQUENCY_OPTIONS.map(freq => (
                        <SelectItem key={freq} value={freq}>
                          {enumTranslations.updateFrequency[freq]}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>

                <div className='min-w-0'>
                  <div className='mb-2 flex items-center gap-2'>
                    <Trash2 className='size-3.5 shrink-0 text-muted-fg' />
                    <Label className='text-xs font-medium'>
                      <Trans>If Source Removed</Trans>
                    </Label>
                    <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                    <SimpleTooltip
                      delayDuration={0}
                      title={t`What happens when the source URL is no longer available. Flag marks it for review, Remove deletes it automatically.`}
                    >
                      <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                    </SimpleTooltip>
                  </div>
                  <div
                    className={cn(
                      isKbAutoUpdatesLocked && 'pointer-events-none',
                    )}
                  >
                    <p className='text-sm'>
                      {folderId ? (
                        <Trans>Inherited from the folder settings</Trans>
                      ) : (
                        enumTranslations.removalBehavior[RemovalBehavior.Flag]
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </details>
          </BasicForm>

          {!embedded && (
            <Dialog.Footer className='z-50 flex flex-row justify-end gap-2 md:flex-row'>
              <Button
                data-cta='admin-add-external-document-add-button'
                disabled={
                  createExternalDocument.isPending ||
                  limitReached ||
                  projectedLimitReached
                }
                loading={createExternalDocument.isPending}
                onClick={handleAdd}
              >
                <Trans>Add</Trans>
              </Button>
            </Dialog.Footer>
          )}
        </>
      );
    },
  ),
);
